// ============================================================
// PlanViewer — le plan, EN HAUT et PLEINE LARGEUR.
// ------------------------------------------------------------
// ── Pourquoi une image et plus une iframe PDF ──
//
// Le lecteur PDF du navigateur impose sa barre d'outils et son panneau
// de miniatures, et on ne les masque pas de façon fiable d'un
// navigateur à l'autre. Sur un A3 paysage dans une demi-largeur, on
// devine le cartouche, on ne le lit pas — c'était le cas.
//
// L'image de la page est **déjà produite** : c'est celle qu'on envoie
// au modèle, redressée. L'afficher donne trois choses :
//
//  1. le contrôle total de la mise en page — pas de barre parasite ;
//  2. aucune dépendance de 350 Ko ;
//  3. et surtout, **Charles voit exactement ce que l'agent a vu**. Le
//     jour où une lecture est fausse, il distingue d'un coup d'œil une
//     erreur de modèle d'une image abîmée. C'est la confusion qui a
//     coûté une demi-journée le 3 août 2026 avec `/Rotate 270` : on
//     regardait le JPEG extrait pendant que le modèle recevait une page
//     couchée.
//
// Le PDF d'origine reste accessible par un lien — pour imprimer, ou
// pour vérifier ce que l'extraction aurait perdu.

import { useCallback, useEffect, useRef, useState } from 'react';
import Button from '../../ui/Button.jsx';
import Badge from '../../ui/Badge.jsx';
import { ErrorState, Skeleton } from '../../ui/States.jsx';
import api, { fetchBlob } from '../../lib/api.js';
import DecoupePlan from './DecoupePlan.jsx';

const ZOOMS = [1, 1.5, 2, 3, 4];

export function PlanViewer({ devisId, pages = [], pageId, onSelectPage, reference = null, decoupe = null }) {
  const [url, setUrl] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [chargement, setChargement] = useState(false);
  const [zoom, setZoom] = useState(1);
  const objets = useRef([]);
  const cadre = useRef(null);
  const glisse = useRef(null);

  // ── LA DÉCOUPE — 14 octobre 2026 ──
  // `origine` dit si la page affichée est une ZONE découpée, et de quelle
  // page ; « Voir la page entière » bascule l'image sans changer de pièce.
  const [origine, setOrigine] = useState(null);
  const [pageEntiere, setPageEntiere] = useState(false);
  const [decoupeOuverte, setDecoupeOuverte] = useState(false);
  const imageId = pageEntiere && origine?.zone_courante ? origine.page.id : pageId;
  const avecDecoupe = Boolean(decoupe); // l'objet change à chaque rendu, pas sa présence

  useEffect(() => {
    setOrigine(null);
    setPageEntiere(false);
    if (!avecDecoupe || !pageId) return undefined;
    let vivant = true;
    api.get(`/devis/${devisId}/plans/${pageId}/decoupe`)
      .then((p) => { if (vivant) setOrigine(p); })
      // Sans elle, on perd le badge d'origine, pas le plan : on le dit
      // dans le journal et l'écran reste utilisable.
      .catch((err) => console.warn('Origine de la page indisponible', err));
    return () => { vivant = false; };
  }, [devisId, pageId, avecDecoupe]);

  const index = pages.findIndex((p) => p.id === pageId);
  const courante = index >= 0 ? pages[index] : null;

  /** Libère toutes les URL objet : sinon chaque page vue reste en mémoire. */
  const liberer = () => {
    for (const u of objets.current) URL.revokeObjectURL(u);
    objets.current = [];
  };

  const charger = useCallback(async () => {
    if (!imageId) return;
    setChargement(true);
    setErreur(null);
    setZoom(1);
    try {
      const blob = await fetchBlob(`/devis/${devisId}/plans/fichier/${imageId}/image`);
      liberer();
      const u = URL.createObjectURL(blob);
      objets.current.push(u);
      setUrl(u);
      setPdfUrl(null);
    } catch (err) {
      setErreur(err);
      setUrl(null);
    } finally {
      setChargement(false);
    }
  }, [devisId, imageId]);

  useEffect(() => {
    charger();
  }, [charger]);

  useEffect(() => liberer, []);

  /** Le PDF d'origine, à la demande — pour imprimer ou tout revoir. */
  async function ouvrirPdf() {
    try {
      const blob = pdfUrl
        ? null
        : await fetchBlob(`/devis/${devisId}/plans/fichier/${imageId}`);
      const u = pdfUrl ?? URL.createObjectURL(blob);
      if (!pdfUrl) {
        objets.current.push(u);
        setPdfUrl(u);
      }
      window.open(u, '_blank', 'noopener');
    } catch (err) {
      setErreur(err);
    }
  }

  // Déplacement au pointeur quand l'image dépasse son cadre.
  const onPointerDown = (e) => {
    glisse.current = { x: e.clientX, y: e.clientY, l: cadre.current.scrollLeft, t: cadre.current.scrollTop };
  };
  const onPointerMove = (e) => {
    if (!glisse.current) return;
    cadre.current.scrollLeft = glisse.current.l - (e.clientX - glisse.current.x);
    cadre.current.scrollTop = glisse.current.t - (e.clientY - glisse.current.y);
  };
  const relacher = () => {
    glisse.current = null;
  };

  if (!pageId) {
    return (
      <div className="plan-viewer plan-viewer--vide">
        <p className="field__hint">
          Déposez un PDF, puis choisissez une page : le plan reste affiché ici pendant que vous
          vérifiez la lecture.
        </p>
      </div>
    );
  }

  return (
    <div className="plan-viewer">
      <div className="plan-viewer__barre">
        <Button size="sm" variant="ghost" disabled={index <= 0} onClick={() => onSelectPage(pages[index - 1].id)}>
          ← Précédente
        </Button>
        {/* L'IDENTITÉ de ce qui est affiché, en clair et sans défiler.
            Un visualiseur titré « Page 2 sur 22 » ne dit pas DE QUELLE
            PIÈCE il montre le plan ; celui-ci le dit, et c'est ce qui
            rend une incohérence impossible à ne pas voir. */}
        <Badge tone="neutral">
          {reference ? `Plan — ${reference}` : `Page ${courante?.page_number ?? '?'}`}
        </Badge>
        <Button size="sm" variant="ghost" disabled={index < 0 || index >= pages.length - 1}
          onClick={() => onSelectPage(pages[index + 1].id)}>
          Suivante →
        </Button>

        <span className="plan-viewer__zoom">
          <Button size="sm" variant="ghost" disabled={zoom <= ZOOMS[0]}
            onClick={() => setZoom(ZOOMS[Math.max(0, ZOOMS.indexOf(zoom) - 1)])}>
            −
          </Button>
          <span className="field__hint">{Math.round(zoom * 100)} %</span>
          <Button size="sm" variant="ghost" disabled={zoom >= ZOOMS.at(-1)}
            onClick={() => setZoom(ZOOMS[Math.min(ZOOMS.length - 1, ZOOMS.indexOf(zoom) + 1)])}>
            +
          </Button>
        </span>

        {url ? (
          <Button size="sm" variant="ghost" onClick={() => window.open(url, '_blank', 'noopener')}>
            Plein écran
          </Button>
        ) : null}
        <Button size="sm" variant="ghost" onClick={ouvrirPdf}>
          Ouvrir le PDF d’origine
        </Button>
        {decoupe && origine && !origine.verrouille ? (
          <Button size="sm" variant="secondary" onClick={() => setDecoupeOuverte(true)}>
            {origine.decoupee ? '✂️ Modifier la découpe' : '✂️ Plusieurs pièces sur ce plan ? Découper'}
          </Button>
        ) : null}
      </div>

      {origine?.zone_courante ? (
        <p className="decoupe-origine">
          <Badge tone="neutral">{`Découpée de la page ${origine.page.page_number ?? '?'}`}</Badge>
          <Button size="sm" variant="ghost" onClick={() => setPageEntiere((v) => !v)}>
            {pageEntiere ? `Revenir à la zone « ${origine.zone_courante.libelle ?? ''} »` : 'Voir la page entière'}
          </Button>
        </p>
      ) : null}
      {decoupeOuverte ? (
        <DecoupePlan devisId={devisId} pageId={pageId} avant={decoupe.avant} apres={decoupe.apres}
          onClose={() => setDecoupeOuverte(false)} />
      ) : null}

      {erreur ? <ErrorState error={erreur} onRetry={charger} title="Image indisponible" /> : null}
      {chargement && !url ? <Skeleton height="55vh" /> : null}
      {url && !erreur ? (
        // Événements POINTEUR et non souris : ils couvrent le doigt et
        // le stylet en plus de la souris, avec un seul jeu de
        // gestionnaires. Et le cadre reste un simple conteneur
        // déroulant — le glisser ne fait que doubler un défilement que
        // le clavier obtient déjà avec les flèches. Lui coller un rôle
        // interactif promettrait une action qui n'existe pas.
        <div
          className="plan-viewer__cadre"
          ref={cadre}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={relacher}
          onPointerLeave={relacher}
        >
          <img
            className="plan-viewer__image"
            src={url}
            alt={`Plan, page ${courante?.page_number ?? ''}, tel que l’agent l’a reçu`}
            style={{ width: `${zoom * 100}%` }}
            draggable={false}
          />
        </div>
      ) : null}
      <p className="plan-viewer__note field__hint">
        C’est l’image exacte reçue par l’agent, redressée. Si elle est illisible ici, elle l’était
        pour lui.
      </p>
    </div>
  );
}

export default PlanViewer;
